/**
 * EVO — BILDIRISHNOMA QO'NG'IROG'I.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ XABARLAR BOR EDI, KO'RADIGAN JOY YO'Q EDI                                ║
 * ║                                                                          ║
 * ║ Skaner har soatda ishlaydi va topganini navbatga yozadi: muddati        ║
 * ║ o'tgan qarz, tugayotgan tovar, ta'minotchiga to'lov muddati, tasdiq      ║
 * ║ so'rovi. Auditda topilganda bazada 29 ta O'QILMAGAN xabar turardi —     ║
 * ║ ularni hech kim hech qachon ko'rmagan.                                   ║
 * ║                                                                          ║
 * ║ Telegram ham yechim emas edi: unga ulanish ekranı ham yo'q edi           ║
 * ║ (`settings/my-settings.tsx` da tuzatildi). Ya'ni ikkala yo'l ham        ║
 * ║ yopiq turardi.                                                           ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ QO'NG'IROQ SANOQNI HAR DAQIQADA EMAS, HAR 2 DAQIQADA SO'RAYDI            │
 * │                                                                          │
 * │ Bildirishnoma — SHOSHILINCH emas: u soatlik skanerdan keladi va bir     │
 * │ daqiqa kechikishi hech narsani o'zgartirmaydi. Har 30 soniyada so'rash  │
 * │ esa 8 soatlik ish kunida 960 ta ortiqcha so'rov degani.                  │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
  markRead,
  notificationKeys,
} from '../../lib/notifications';
import { useT } from '../../lib/i18n-context';

const POLL_MS = 120_000;

export function NotificationBell(): ReactNode {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const count = useQuery({
    queryKey: notificationKeys.unread,
    queryFn: ({ signal }) => fetchUnreadCount(signal),
    refetchInterval: POLL_MS,
  });

  const list = useQuery({
    queryKey: notificationKeys.list,
    queryFn: ({ signal }) => fetchNotifications(signal),
    // Ro'yxat FAQAT ochilganda so'raladi: yopiq qo'ng'iroq uchun sanoq yetadi.
    enabled: open,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: notificationKeys.all });
  const read = useMutation({ mutationFn: (id: number) => markRead(id), onSuccess: refresh });
  const readAll = useMutation({ mutationFn: () => markAllRead(), onSuccess: refresh });

  /*
   * Tashqariga bosilganda yopiladi. `mousedown`, `click` EMAS: ichkaridagi
   * tugma bosilganda `click` avval hujjatga yetib, oynani yopib qo'yardi
   * va bosish yo'qolardi.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const unread = count.data?.unread ?? 0;

  return (
    <div ref={boxRef} className="relative shrink-0">
      <button
        type="button"
        aria-label={t('notif.title')}
        title={t('notif.title')}
        onClick={() => setOpen((v) => !v)}
        /*
         * Qo'ng'iroqning O'ZI — korpus rangida (`chrome-*`), chunki u
         * yuqori panelning ichida, jade fon ustida turadi. Ostidagi
         * ro'yxat esa OQ qoladi (pastga qarang): u ish maydoni ustiga
         * suzadi, ya'ni boshqa qatlam.
         */
        className="relative flex size-9 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-chrome-2 hover:text-chrome-ink motion-reduce:transition-none"
      >
        <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
          <path
            d="M12 3a6 6 0 0 0-6 6v3.6l-1.3 2.6A1 1 0 0 0 5.6 17h12.8a1 1 0 0 0 .9-1.8L18 12.6V9a6 6 0 0 0-6-6Zm0 18a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 21Z"
            fill="currentColor"
          />
        </svg>
        {/*
          * Sanoq 99 dan oshsa «99+» — uch xonali raqam doirani cho'zib,
          * qolgan ikonkalarni surib yuborardi.
          *
          * ╔══════════════════════════════════════════════════════════════════╗
          * ║ `text-[10px]` SHKALAGA QAYTARILDI                                ║
          * ║                                                                  ║
          * ║ Bu butun ilovadagi YAGONA qattiq yozilgan shkaladan tashqari     ║
          * ║ o'lcham edi. Zarari aniq: ega matnni ikki marta kattalashtirishni║
          * ║ so'radi va `styles.css` dagi `--text-*` qiymatlari o'zgardi —    ║
          * ║ bu nishon esa har ikkalasini ham E'TIBORGA OLMADI va o'qib       ║
          * ║ bo'lmaydigan bo'lib qolaverdi.                                   ║
          * ║                                                                  ║
          * ║ Endi `text-xs` (14 px). Raqam kattalashgani uchun uyaning        ║
          * ║ o'lchami ham oshdi: `min-w-5` + `leading-5` — bir xonali sanoq   ║
          * ║ 20 px lik DOIRA bo'lib qoladi (kvadrat emas), ikki xonali va     ║
          * ║ «99+» esa `px-1` hisobiga yonga cho'ziladi. Nishon `absolute`,   ║
          * ║ ya'ni cho'zilish yuqoridagi ikonkalarni surib yubormaydi —       ║
          * ║ tepadagi mulohaza kuchda qoladi.                                 ║
          * ╚══════════════════════════════════════════════════════════════════╝
          */}
        {/*
          * ╔══════════════════════════════════════════════════════════════════╗
          * ║ NISHON RANGI `bg-danger` DAN `bg-chrome-danger` GA O'TDI         ║
          * ║                                                                  ║
          * ║ `--color-danger` (#C3281D) OQ fon uchun tanlangan qizil. Yuqori  ║
          * ║ panel chuqur jadega (#0B3D29) o'tgach u FON bilan 2.12 kontrast  ║
          * ║ berdi — ya'ni qizil doira yashil fonda qora dog' bo'lib qolardi  ║
          * ║ va aynan «ko'zga tashlanish» vazifasini bajarmasdi.              ║
          * ║                                                                  ║
          * ║ `chrome-danger` (#FF8F8A) — o'sha vazifa uchun to'q fonga        ║
          * ║ tanlangan pog'ona: fon bilan 5.57. Matn esa OQ emas, JADE        ║
          * ║ (`text-chrome`): oq #FF8F8A ustida 2.2 ga tushardi.              ║
          * ║                                                                  ║
          * ║ Ustiga, endi nishon mavzuga bog'liq emas — qorong'i mavzuda      ║
          * ║ `--color-danger` pushti (#FB7185) ga aylanardi va bitta panel    ║
          * ║ kunduzi bir xil, kechqurun boshqa rangda turardi.                ║
          * ╚══════════════════════════════════════════════════════════════════╝
          */}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex min-w-5 items-center justify-center rounded-full bg-chrome-danger px-1 text-xs leading-5 font-bold text-chrome">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/*
        ⚠️ Ro'yxat ATAYLAB oq sirtda qoladi (`bg-surface`), korpus rangida
        emas: u panelning ichida emas, ISH MAYDONI ustida suzadi va uzun
        matnli xabarlarni ko'rsatadi. Uni jade qilish o'qish uchun
        mo'ljallanmagan yuzada uzun matn o'qitish bo'lardi; ustiga, ekranda
        «panel qayerda tugadi» chegarasi ham yo'qolardi.
      */}
      {open && (
        <div className="evo-raised absolute right-0 z-50 mt-2 w-80 rounded-xl border border-line bg-surface p-2 sm:w-96">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-sm font-semibold">{t('notif.title')}</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending}
                className="text-xs text-brand hover:underline"
              >
                {t('notif.readAll')}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-auto">
            {list.isPending ? (
              <p className="px-2 py-4 text-sm text-ink-muted">{t('common.loading')}</p>
            ) : (list.data?.items.length ?? 0) === 0 ? (
              <p className="px-2 py-4 text-sm text-ink-muted">{t('notif.empty')}</p>
            ) : (
              <ul className="space-y-1">
                {(list.data?.items ?? []).map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => n.readAt === null && read.mutate(n.id)}
                      className={`w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-surface-muted motion-reduce:transition-none ${
                        n.readAt === null ? 'bg-brand-soft/40' : ''
                      }`}
                    >
                      {/*
                        * Matn SERVERDAN tayyor keladi (emoji, satrlar bilan)
                        * va `whitespace-pre-line` uni o'sha ko'rinishda
                        * chizadi. Qayta formatlash ikkinchi haqiqat manbai
                        * bo'lardi: Telegramdagi xabar bilan ekrandagi xabar
                        * bir xil bo'lishi kerak.
                        */}
                      <span className="block text-xs whitespace-pre-line text-ink">{n.text}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
